import React, { useEffect, useMemo, useState } from "react";
import { FiTag } from "react-icons/fi";
import { useFormModal } from "@core/hooks/useFormModal";
import { FormModal, FormModalField } from "@core/components/FormModal";
import { UpdateReportTagRequest } from "@features/report/tag/types/reportTagRequests";
import { useReportTagActions } from "@features/report/tag/hooks/useReportTagActions";
import { useReportTagStatus } from "@features/report/tag/hooks/useReportTags";
import { ReportTag } from "@features/report/tag/types/reportTag";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { getReportTagStatusLabel } from "@core/reports/utils/reportWorkflow";
import { ErrorResponse } from "@core/types/errorResponse";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import CloseTagBlockedModal from "@features/report/tag/components/CloseTagBlockedModal";
import CloseTagConfirmModal from "@features/report/tag/components/CloseTagConfirmModal";
import { DepartmentReportStatusSummary } from "@features/report/tag/types/departmentReportStatusSummary";
import { hasSoftLeftoverCounts } from "@features/report/tag/utils/departmentReportStatusCounts";
import {
    fromDatetimeLocalValue,
    isDeadlineOnOrAfterUntil,
    toDatetimeLocalValue,
} from "@features/report/tag/utils/reportTagDeadline";

type EditTagForm = {
    name: string;
    description: string;
    since: string;
    until: string;
    deadline: string;
    report_tag_status_id: number | string;
};

const EMPTY_FORM: EditTagForm = {
    name: "",
    description: "",
    since: "",
    until: "",
    deadline: "",
    report_tag_status_id: "",
};

const UpdateTagModal: React.FC<{
    show: boolean;
    onHide: () => void;
    tag: ReportTag | null;
    onUpdated?: () => void;
}> = ({ show, onHide, tag, onUpdated }) => {
    const resolvedNationId = Number(useScopeId()); // scope_ref_id
    const { busy: actionBusy, doTagUpdate, doFetchDepartmentReportStatusSummary } = useReportTagActions();
    const { data: statuses } = useReportTagStatus({ all: true });
    const showError = useApiError();
    const showValidation = useErrorModalStore((s) => s.showValidation);

    const [showBlocked, setShowBlocked] = useState(false);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [statusSummary, setStatusSummary] = useState<DepartmentReportStatusSummary | null>(null);
    const [pendingPayload, setPendingPayload] = useState<UpdateReportTagRequest | null>(null);
    const [closeBusy, setCloseBusy] = useState(false);

    const buildPayload = (f: EditTagForm): UpdateReportTagRequest => ({
        name: f.name,
        description: f.description,
        since: f.since || null,
        until: f.until || null,
        deadline: fromDatetimeLocalValue(f.deadline),
        report_tag_status_id: Number(f.report_tag_status_id) || 0,
    });

    const isClosingToClosed = (f: EditTagForm): boolean => {
        if (!tag) return false;
        const selected = statuses.find(
            (s: any) => String(s.report_tag_status_id) === String(f.report_tag_status_id)
        );
        const selectedName = (selected?.status_name ?? "").toUpperCase();
        const currentName = (tag.report_tag_status_name ?? "").toUpperCase();
        return selectedName === "CLOSED" && currentName !== "CLOSED";
    };

    const finishSuccess = () => {
        onHide();
        onUpdated?.();
    };

    const performUpdate = async (payload: UpdateReportTagRequest) => {
        const response = await doTagUpdate(resolvedNationId, tag!.report_tag_id, payload);
        if (response.isSuccess()) {
            showSuccess(response.getData()?.message);
            finishSuccess();
            return true;
        }
        showError(response.getError() as ErrorResponse);
        return false;
    };

    const { form, formErrors, busy, handleChange, setFieldValue, setForm, handleSubmit } =
        useFormModal<EditTagForm>({
            initial: EMPTY_FORM,
            validate: (f) => {
                const messages: string[] = [];
                const errors: Partial<Record<keyof EditTagForm, string[]>> = {};
                if (!tag) messages.push("No tag selected.");
                if (!f.name?.trim()) {
                    messages.push("Tag name is required.");
                    errors.name = ["Tag name is required."];
                }
                if (tag && isUnchanged(f, tag)) messages.push("No changes to save.");
                if (f.deadline && f.until && !isDeadlineOnOrAfterUntil(f.deadline, f.until)) {
                    messages.push("Deadline must be on or after the until date.");
                    errors.deadline = ["Deadline must be on or after the until date."];
                }
                return { errors, messages };
            },
            submit: (f) => doTagUpdate(resolvedNationId, tag!.report_tag_id, buildPayload(f)),
            onSuccess: finishSuccess,
            errorFallback: "Failed to update tag",
        });

    useEffect(() => {
        setForm(
            tag
                ? {
                      name: tag.name ?? "",
                      description: tag.description ?? "",
                      since: tag.since ?? "",
                      until: tag.until ?? "",
                      deadline: toDatetimeLocalValue(tag.deadline),
                      report_tag_status_id: Number(tag.report_tag_status_id) || "",
                  }
                : EMPTY_FORM
        );
        setShowBlocked(false);
        setShowCloseConfirm(false);
        setStatusSummary(null);
        setPendingPayload(null);
    }, [tag, setForm, show]);

    const fields: FormModalField<EditTagForm>[] = useMemo(
        () => [
            {
                name: "name",
                label: "Tag Name",
                type: "text",
                required: true,
                placeholder: "Enter Tag name",
                helpText: "A name for this report period, e.g. January 2025 or Q1 2025. Shown when selecting which period to report on.",
            },
            {
                name: "since",
                label: "Since Date",
                type: "date",
                required: true,
                placeholder: "Enter Since date",
                helpText: "First day of the reporting period this tag covers.",
            },
            {
                name: "until",
                label: "Until Date",
                type: "date",
                required: true,
                placeholder: "Enter Until date",
                helpText: "Last day of the reporting period this tag covers.",
            },
            {
                name: "deadline",
                label: "Deadline",
                type: "datetime-local",
                placeholder: "Deadline",
                helpText: "Optional last moment to submit without being late. Clear to leave unset.",
            },
            {
                name: "report_tag_status_id",
                label: "Status",
                type: "select",
                required: true,
                placeholder: "Select status",
                helpText: "Open: users can submit reports. Closed: no more submissions; reopen can be requested if needed.",
                options: statuses.map((s: any) => ({
                    label: getReportTagStatusLabel(s.status_name),
                    value: s.report_tag_status_id,
                })),
            },
            {
                name: "description",
                label: "Description",
                type: "textarea",
                placeholder: "Enter Tag description",
                helpText: "Optional notes about this report period.",
            },
        ],
        [statuses]
    );

    const handleUpdateClick = async () => {
        const messages: string[] = [];
        if (!tag) messages.push("No tag selected.");
        if (!form.name?.trim()) messages.push("Tag name is required.");
        if (tag && isUnchanged(form, tag)) messages.push("No changes to save.");
        if (form.deadline && form.until && !isDeadlineOnOrAfterUntil(form.deadline, form.until)) {
            messages.push("Deadline must be on or after the until date.");
        }
        if (messages.length > 0) {
            showValidation(messages);
            return;
        }

        if (!isClosingToClosed(form)) {
            await handleSubmit();
            return;
        }

        const payload = buildPayload(form);
        setCloseBusy(true);
        try {
            const summaryResponse = await doFetchDepartmentReportStatusSummary(
                resolvedNationId,
                tag!.report_tag_id
            );
            if (!summaryResponse.isSuccess()) {
                showError(summaryResponse.getError() as ErrorResponse);
                return;
            }

            const summary = summaryResponse.getData() as DepartmentReportStatusSummary;
            setStatusSummary(summary);

            if ((summary.blocking_count ?? 0) > 0) {
                setShowBlocked(true);
                return;
            }

            if (hasSoftLeftoverCounts(summary.counts ?? [])) {
                setPendingPayload(payload);
                setShowCloseConfirm(true);
                return;
            }

            await performUpdate(payload);
        } catch (error) {
            showError(error as ErrorResponse);
        } finally {
            setCloseBusy(false);
        }
    };

    const handleConfirmClose = async () => {
        if (!pendingPayload || !tag) return;
        setCloseBusy(true);
        try {
            const ok = await performUpdate(pendingPayload);
            if (ok) {
                setShowCloseConfirm(false);
                setPendingPayload(null);
            }
        } catch (error) {
            showError(error as ErrorResponse);
        } finally {
            setCloseBusy(false);
        }
    };

    const modalBusy = busy || actionBusy || closeBusy;

    return (
        <>
            <FormModal<EditTagForm>
                show={show}
                onHide={onHide}
                title="Update Report Tag"
                icon={<FiTag aria-hidden />}
                fields={fields}
                form={form}
                formErrors={formErrors}
                busy={modalBusy}
                onChange={handleChange}
                setFieldValue={setFieldValue}
                onSubmit={handleUpdateClick}
                submitLabel="Update Tag"
                busyLabel="Updating Tag..."
                submitIcon={null}
                testId="update-tag-modal"
            />
            <CloseTagBlockedModal
                show={showBlocked}
                onHide={() => setShowBlocked(false)}
                summary={statusSummary}
            />
            <CloseTagConfirmModal
                show={showCloseConfirm}
                onHide={() => {
                    setShowCloseConfirm(false);
                    setPendingPayload(null);
                }}
                onConfirm={handleConfirmClose}
                busy={closeBusy || actionBusy}
                tagName={tag?.name}
                summary={statusSummary}
            />
        </>
    );
};

/** True when the edited form matches the original tag (nothing to save). */
function isUnchanged(form: EditTagForm, original: ReportTag): boolean {
    return (
        (form.name ?? "") === (original.name ?? "") &&
        (form.description ?? "") === (original.description ?? "") &&
        (form.since ?? "") === (original.since ?? "") &&
        (form.until ?? "") === (original.until ?? "") &&
        (form.deadline ?? "") === toDatetimeLocalValue(original.deadline) &&
        String(form.report_tag_status_id ?? 0) === String(original.report_tag_status_id ?? 0)
    );
}

export default UpdateTagModal;
